import { createSelector, createSlice, PayloadAction, prepareAutoBatched } from '@reduxjs/toolkit';
import {
  CustomControlConfig,
  CustomTileData,
  HoverParent,
  LayoutConfigState,
  ShowHoverChildrenChangedPayload,
} from 'features/layout/features/config/types';
import { LayoutMode, SourceKind, VideoTileLayoutMode } from 'features/layout/types';
import { SDKConnected } from 'features/sdk/sdkStateSlice';
import { RootState } from 'store/store';
import { authorizeUser } from 'features/room/thunks/authorizeUser';

export const initialState: LayoutConfigState = {
  currentMode: LayoutMode.tiled,
  recordingMode: LayoutMode.tiled,
  restreamingMode: LayoutMode.tiled,
  videoTileLayoutMode: VideoTileLayoutMode.top,
  minimizeLocal: false,
  showScreensharePreview: false,
  modeControlEnabled: false,
  minimizeOwnTileOnJoinEnabled: false,
  participantNamesInRecordingsEnabled: false,
  participantNamesInRestreamingEnabled: false,
  hideTilesInRecordingsRoles: [],
  hideTilesInRestreamingEnabled: false,
  captionsInRecordingsEnabled: false,
  showHoverChildren: {
    gridPanel: true,
    tiles: true,
  },
  zenMode: true,
  customControls: {
    all: {},
    local: {},
    remote: {},
    'screenshare-local': {},
    'screenshare-remote': {},
    custom: {},
    external: {},
  },
  customControlScopes: {},
  customTiles: {},
  customTilePositions: {
    first: {},
    last: {},
  },
  broadcasterTileBehaviour: 'all',
};

const layoutConfigSlice = createSlice({
  name: 'layoutConfig',
  initialState,
  reducers: {
    screensharePreviewToggled(state) {
      state.showScreensharePreview = !state.showScreensharePreview;
    },
    localStreamExpanded(state) {
      state.minimizeLocal = false;
    },
    localStreamCollapsed(state) {
      state.minimizeLocal = true;
    },
    layoutModeChanged(state, action: PayloadAction<LayoutMode>) {
      state.currentMode = action.payload;

      if (state.currentMode === LayoutMode.auto) {
        state.hideLayoutControls = true;
      } else {
        state.hideLayoutControls = undefined;
      }
    },
    zenModeChanged: {
      reducer(state, action: PayloadAction<boolean>) {
        state.zenMode = action.payload;

        state.showHoverChildren.tiles = !state.zenMode;
        state.showHoverChildren.gridPanel = !state.zenMode;
      },
      prepare: prepareAutoBatched<boolean>(),
    },
    showHoverChildrenChanged(state, action: PayloadAction<ShowHoverChildrenChangedPayload>) {
      if (state.zenMode) {
        state.showHoverChildren[action.payload.area] = action.payload.show;
      } else {
        state.showHoverChildren[action.payload.area] = true;
      }
    },
    customControlAdded(state, action: PayloadAction<CustomControlConfig>) {
      state.customControls[action.payload.properties.scope][action.payload.name] = action.payload;
      state.customControlScopes[action.payload.name] = action.payload.properties.scope;
    },
    customControlRemoved(state, action: PayloadAction<string>) {
      const scope = state.customControlScopes[action.payload];

      if (scope) {
        delete state.customControls[scope][action.payload];
        delete state.customControlScopes[action.payload];
      }
    },
    customTileAdded(state, action: PayloadAction<CustomTileData>) {
      const existTile = state.customTiles[action.payload.name];
      if (existTile && action.payload.position !== existTile.position) {
        delete state.customTilePositions[existTile.position][existTile.name];
      }
      state.customTilePositions[action.payload.position][action.payload.name] = action.payload.name;
      state.customTiles[action.payload.name] = action.payload;
    },
    customTileRemoved(state, action: PayloadAction<string>) {
      if (state.customTiles[action.payload]) {
        const { position } = state.customTiles[action.payload];
        delete state.customTilePositions[position][action.payload];
        delete state.customTiles[action.payload];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authorizeUser.fulfilled, (state, action) => {
        const {
          layoutModeSwitchEnabled,
          recordingsLayoutMode,
          minimizeOwnTileOnJoinEnabled,
          captionsInRecordingsEnabled,
          participantNamesInRecordingsEnabled,
          hideTilesInRecordingsRoles,
          broadcasterTileVisibility,
          restreaming,
          videoTileLayoutMode,
        } = action.payload.settings;

        state.videoTileLayoutMode = videoTileLayoutMode ?? VideoTileLayoutMode.top;
        state.modeControlEnabled = layoutModeSwitchEnabled;
        state.recordingMode = recordingsLayoutMode;
        state.minimizeOwnTileOnJoinEnabled = minimizeOwnTileOnJoinEnabled;
        state.captionsInRecordingsEnabled = captionsInRecordingsEnabled;
        state.participantNamesInRecordingsEnabled = participantNamesInRecordingsEnabled;
        state.hideTilesInRecordingsRoles = hideTilesInRecordingsRoles ?? [];
        state.broadcasterTileBehaviour = broadcasterTileVisibility;

        state.restreamingMode = restreaming.layoutMode;
        state.participantNamesInRestreamingEnabled = restreaming.participantNamesEnabled;
        state.hideTilesInRestreamingEnabled = restreaming.hideTiles;
      })
      .addCase(SDKConnected, (state, action) => {
        const options = action.payload;
        if (options.layoutMode !== undefined) {
          state.currentMode = options.layoutMode;

          if (state.currentMode === LayoutMode.auto) {
            state.hideLayoutControls = true;
          } else {
            state.hideLayoutControls = undefined;
          }
        }
      });
  },
});

export const {
  screensharePreviewToggled,
  localStreamExpanded,
  localStreamCollapsed,
  layoutModeChanged,
  zenModeChanged,
  showHoverChildrenChanged,
  customControlAdded,
  customControlRemoved,
  customTileAdded,
  customTileRemoved,
} = layoutConfigSlice.actions;

export const selectVideoTileLayoutMode = (state: RootState) =>
  state.layout.config.videoTileLayoutMode;

export const selectIsBottomTileLayout = (state: RootState) =>
  state.layout.config.videoTileLayoutMode === VideoTileLayoutMode.bottom;

export const selectCurrentLayoutMode = (state: RootState) => state.layout.config.currentMode;
export const selectCurrentRecordingMode = (state: RootState) => state.layout.config.recordingMode;
export const selectShowCaptionsInRecordings = (state: RootState) =>
  state.layout.config.captionsInRecordingsEnabled;
export const selectShowScrensharePreview = (state: RootState) =>
  state.layout.config.showScreensharePreview;
export const selectLocalStreamMinimized = (state: RootState) => state.layout.config.minimizeLocal;

export const selectHideLayoutControls = (state: RootState) =>
  state.layout.config.hideLayoutControls;

export const selectLayoutModeControlEnabled = (state: RootState) =>
  state.layout.config.modeControlEnabled;

export const selectMinimizeLocalOnJoin = (state: RootState) =>
  state.layout.config.minimizeOwnTileOnJoinEnabled;

export const selectShowParticipantNamesInRecording = (state: RootState) =>
  state.layout.config.participantNamesInRecordingsEnabled;

export const selectZenModeEnabled = (state: RootState) => state.layout.config.zenMode;

export const selectShowHoverChildren = (state: RootState, area: HoverParent) =>
  state.layout.config.showHoverChildren[area];

export const selectCustomControls = createSelector(
  [
    (state: RootState) => state.layout.config.customControls,
    (_: RootState, scope: SourceKind) => scope,
  ],
  (customControls, scope) => {
    if (scope === 'debug') {
      return {};
    }

    const sharedControls = customControls.all;

    return {
      ...sharedControls,
      ...customControls[scope],
    };
  }
);

export const selectCustomTiles = createSelector(
  [
    (state: RootState) => state.layout.content.maximizedStream,
    (state: RootState) => state.layout.config.customTilePositions,
    (state: RootState) => state.layout.config.customTiles,
  ],
  (maximizedStream, customTilePositions, customTilesData) => {
    const firstCustomTiles = Object.values(customTilePositions.first);
    const lastCustomTiles = Object.values(customTilePositions.last);

    const formattedFirstCustomTiles =
      maximizedStream?.kind === 'custom' && customTilePositions.first[maximizedStream.userId]
        ? firstCustomTiles.filter((name) => name !== maximizedStream?.userId)
        : firstCustomTiles;

    const formattedLastCustomTiles =
      maximizedStream?.kind === 'custom' && customTilePositions.last[maximizedStream.userId]
        ? lastCustomTiles.filter((name) => name !== maximizedStream?.userId)
        : lastCustomTiles;

    return {
      first: formattedFirstCustomTiles.map((name) => customTilesData[name]),
      last: formattedLastCustomTiles.map((name) => customTilesData[name]),
    };
  }
);

export const selectCustomTilesCount = createSelector(
  [selectCustomTiles],
  ({ first, last }) => first.length + last.length
);

export const selectCustomTileById = (state: RootState, id: string) =>
  state.layout.config.customTiles[id];

export const selectCustomTilesData = (state: RootState) => state.layout.config.customTiles;

export const selectBroadcasterTileVisibility = (state: RootState) =>
  state.layout.config.broadcasterTileBehaviour;

export default layoutConfigSlice.reducer;
